import { useState } from "react";
import { ArrowRight, XCircle } from "lucide-react";
import { BUSINESS_TYPES, REVENUE_RANGES, LOGO_URL } from "../../data/quizData";

export const QualificationStep = ({ onQualified, onDisqualified }) => {
  const [businessType, setBusinessType] = useState("");
  const [monthlyRevenue, setMonthlyRevenue] = useState("");
  const [disqualified, setDisqualified] = useState(null);

  const handleContinue = () => {
    if (businessType === "startup") {
      setDisqualified({
        title: "This Audit is for Established Businesses",
        message: "This audit is designed for established businesses generating consistent revenue. Return when you have validated your offer and are generating monthly revenue."
      });
      onDisqualified && onDisqualified("startup");
      return;
    }

    if (monthlyRevenue === "under-15k") {
      setDisqualified({
        title: "Revenue Threshold Not Met",
        message: "This diagnostic is built for businesses ready to scale strategically. Focus on reaching $15,000+ monthly revenue, then return for your growth audit."
      });
      onDisqualified && onDisqualified("revenue");
      return;
    }

    if (businessType && monthlyRevenue) {
      onQualified({ businessType, monthlyRevenue });
    }
  };

  const isValid = businessType && monthlyRevenue;

  if (disqualified) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-12">
        <div className="max-w-lg w-full animate-fadeIn">
          <div className="flex justify-center mb-8">
            <img src={LOGO_URL} alt="Zaharlab" className="h-16 w-auto" />
          </div>
          
          <div className="disqualification-message">
            <XCircle className="w-16 h-16 text-red-400 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-white mb-4">{disqualified.title}</h2>
            <p className="text-slate-400 text-lg leading-relaxed">{disqualified.message}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="max-w-2xl w-full">
        {/* Logo */}
        <div className="flex justify-center mb-10 animate-fadeIn">
          <img src={LOGO_URL} alt="Zaharlab" className="h-20 w-auto" data-testid="logo" />
        </div>

        {/* Header */}
        <div className="text-center mb-12 animate-fadeIn delay-100">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight">
            8-Stage Customer Journey
            <span className="block gradient-text">Audit</span>
          </h1>
          <p className="text-slate-400 text-lg max-w-xl mx-auto">
            A strategic diagnostic for established service businesses ready to identify and eliminate growth barriers.
          </p>
        </div>

        {/* Divider */}
        <div className="divider-glow mb-12" />

        {/* Qualification Form */}
        <div className="stage-card animate-slideInUp delay-200">
          <h2 className="text-xl font-semibold text-white mb-2">Qualification</h2>
          <p className="text-slate-400 mb-8">This audit is designed for established businesses with proven revenue.</p>

          {/* Business Type */}
          <div className="mb-8">
            <label className="block text-slate-300 font-medium mb-4">
              What type of business do you run?
            </label>
            <div className="space-y-3">
              {BUSINESS_TYPES.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setBusinessType(type.value)}
                  className={`answer-option w-full text-left ${
                    businessType === type.value ? "selected" : ""
                  }`}
                  data-testid={`business-type-${type.value}`}
                >
                  <span className="text-slate-200 font-medium">{type.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Monthly Revenue */}
          <div className="mb-8">
            <label className="block text-slate-300 font-medium mb-4">
              What is your average monthly revenue?
            </label>
            <div className="space-y-3">
              {REVENUE_RANGES.map((range) => (
                <button
                  key={range.value}
                  type="button"
                  onClick={() => setMonthlyRevenue(range.value)}
                  className={`answer-option w-full text-left ${
                    monthlyRevenue === range.value ? "selected" : ""
                  }`}
                  data-testid={`revenue-${range.value}`}
                >
                  <span className="text-slate-200 font-medium">{range.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Continue Button */}
          <button
            onClick={handleContinue}
            disabled={!isValid}
            className={`cta-button w-full ${
              !isValid ? "opacity-50 cursor-not-allowed" : ""
            }`}
            data-testid="qualification-continue-btn"
          >
            Begin Diagnostic
            <ArrowRight className="ml-2 w-5 h-5" />
          </button>
        </div>

        {/* Footer note */}
        <p className="text-center text-slate-500 text-sm mt-8 animate-fadeIn delay-300">
          Estimated completion time: 5-7 minutes
        </p>
      </div>
    </div>
  );
};
